<template>
  <!-- The browser picks the mobile image (<source media>): server and client render the same HTML, where choosing
       it with useCustomMediaQuery() left the desktop image on mobile (attributes are not fixed at hydration) -->
  <picture
    v-if="desktop"
    class="header-image"
  >
    <source
      v-if="mobile"
      :media="MOBILE_MEDIA"
      :srcset="mobile.srcset"
      :sizes="mobile.sizes"
    />
    <img
      :src="desktop.src"
      :srcset="desktop.srcset"
      :sizes="desktop.sizes"
      :alt="alt"
      fetchpriority="high"
      v-bind="{ ...$attrs, ...scopeAttrs }"
    />
  </picture>
</template>

<script setup lang="ts">
defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    image?: string
    imageMobile?: string
    alt?: string
    // false: the URLs as given (headers that never went through NuxtImg)
    optimized?: boolean
  }>(),
  {
    image: undefined,
    imageMobile: undefined,
    alt: '',
    optimized: true,
  },
)

// isMobile || isLargeMobile of useCustomMediaQuery, the screens that got the mobile image
const MOBILE_MEDIA = '(max-width: 415px)'

const IMAGE_SIZES = 'xs:100vw sm:100vw md:100vw lg:100vw xl:100vw'
// same keys in the same order as the modifiers NuxtImg builds (@nuxt/image _base.mjs) for quality="80" format="auto":
// the Cloudflare URL lists them in that order, and another order is another transformation billed
const IMAGE_MODIFIERS = {
  width: undefined,
  height: undefined,
  format: 'auto',
  quality: '80',
  background: undefined,
  fit: undefined,
}

// the headers style the img with scoped CSS (.background-image), as when it was a NuxtImg, but Vue puts their scope
// attribute on the root <picture> only: without it on the img, the header image lost its cover layout
const scopeId = getCurrentInstance()?.vnode.scopeId
const scopeAttrs = scopeId ? { [scopeId]: '' } : {}

const img = useImage()

const sources = (url: string) =>
  props.optimized
    ? img.getSizes(url, { sizes: IMAGE_SIZES, modifiers: IMAGE_MODIFIERS })
    : { src: url, srcset: undefined, sizes: undefined }

const desktop = computed(() => (props.image ? sources(props.image) : undefined))
const mobile = computed(() =>
  props.imageMobile && props.imageMobile !== props.image
    ? (() => {
        const { srcset, sizes, src } = sources(props.imageMobile)
        return { srcset: srcset || src, sizes }
      })()
    : undefined,
)
</script>

<style lang="scss">
// the img keeps its place in the header layout, as without the wrapper
.header-image {
  display: contents;
}
</style>
